import authenticatedAxiosInstance from './authenticatedAxiosInstance.service';
import authHeader from './auth-header';
import {
  FPRate,
  LPRate,
  BBRate,
  OriginationFee,
  UnitFeeThreshold,
  OrgFeeTier,
} from '../types/Company.type';

const API_URL = process.env.REACT_APP_API_URL + '/company';

const routes = {
  getAll: `${API_URL}`,
  getByRecId: `${API_URL}/:recId`,
  getRisk: `${API_URL}/:recId/risk`,
  getSelectChoice: `${API_URL}/select-choice`,
};

class CompanyService {
  getCompanies(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(routes.getAll, { headers: authHeader(), signal });
  }

  getCompaniesSelectChoice(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(routes.getSelectChoice, {
      headers: authHeader(),
      signal,
    });
  }

  getCompanyByRecId(RecId: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${API_URL}/${RecId}`, { headers: authHeader(), signal });
  }

  createCompany(data: any) {
    return authenticatedAxiosInstance.post(`${API_URL}`, data, {
      headers: authHeader(),
    });
  }

  updateCompany(data: any) {
    return authenticatedAxiosInstance.put(`${API_URL}/${data.RecId}`, data, {
      headers: authHeader(),
    });
  }

  getRiskByRecId(CompId?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${process.env.REACT_APP_API_URL}/dealer/risk`, {
      headers: authHeader(),
      params: { CompId },
      signal,
    });
  }

  createRisk(data: any) {
    return authenticatedAxiosInstance.post(`${process.env.REACT_APP_API_URL}/dealer/risk`, data, {
      headers: authHeader(),
    });
  }

  updateRisk(data: any) {
    return authenticatedAxiosInstance.put(`${process.env.REACT_APP_API_URL}/dealer/risk`, data, {
      headers: authHeader(),
    });
  }

  getRateNotifications(CompId: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${process.env.REACT_APP_API_URL}/ratenotification`, {
      headers: authHeader(),
      params: { CompId },
      signal,
    });
  }

  createRateNotification(data: any) {
    return authenticatedAxiosInstance.post(
      `${process.env.REACT_APP_API_URL}/ratenotification`,
      data,
      { headers: authHeader() }
    );
  }

  updateRateNotification(RecId: number, data: any) {
    return authenticatedAxiosInstance.put(
      `${process.env.REACT_APP_API_URL}/ratenotification/${RecId}`,
      data,
      { headers: authHeader() }
    );
  }

  deleteRateNotification(RecId: number) {
    return authenticatedAxiosInstance.delete(
      `${process.env.REACT_APP_API_URL}/ratenotification/${RecId}`,
      { headers: authHeader() }
    );
  }

  getPortalUsers(CompId?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${process.env.REACT_APP_API_URL}/dealer/portal-users`, {
      headers: authHeader(),
      params: { CompId },
      signal,
    });
  }

  getDealerUser(RecId: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(
      `${process.env.REACT_APP_API_URL}/dealer/users/${RecId}`,
      { headers: authHeader(), signal }
    );
  }

  createDealerUser(data: any) {
    return authenticatedAxiosInstance.post(`${process.env.REACT_APP_API_URL}/dealer/users`, data, {
      headers: authHeader(),
    });
  }

  updateDealerUser(RecId: number, data: any) {
    return authenticatedAxiosInstance.put(
      `${process.env.REACT_APP_API_URL}/dealer/users/${RecId}`,
      data,
      { headers: authHeader() }
    );
  }

  deleteDealerUser(RecId: number) {
    return authenticatedAxiosInstance.delete(
      `${process.env.REACT_APP_API_URL}/dealer/users/${RecId}`,
      { headers: authHeader() }
    );
  }

  // Portal User methods (uses UserId in body, not RecId in path)
  createPortalUser(data: any) {
    return authenticatedAxiosInstance.post(
      `${process.env.REACT_APP_API_URL}/dealer/portal-users`,
      data,
      { headers: authHeader() }
    );
  }

  updatePortalUser(data: any) {
    return authenticatedAxiosInstance.put(
      `${process.env.REACT_APP_API_URL}/dealer/portal-users`,
      data,
      { headers: authHeader() }
    );
  }

  deletePortalUser(RecId: string) {
    return authenticatedAxiosInstance.delete(
      `${process.env.REACT_APP_API_URL}/dealer/portal-users`,
      { headers: authHeader(), params: { RecId } }
    );
  }

  getPortalUsersMFA(companyId: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(
      `${process.env.REACT_APP_API_URL}/dealer/portal-users/mfa`,
      { headers: authHeader(), params: { CompId: companyId }, signal }
    );
  }

  updatePortalUsersMFA(companyId: string, enabled: boolean) {
    return authenticatedAxiosInstance.put(
      `${process.env.REACT_APP_API_URL}/dealer/portal-users/mfa`,
      { companyId, enabled },
      { headers: authHeader() }
    );
  }

  getDealerContacts(CompId: string, searchText?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${process.env.REACT_APP_API_URL}/dealer/contacts`, {
      headers: authHeader(),
      params: { CompId, searchText },
      signal,
    });
  }

  getCorporateDocs(CompId: string, searchText?: string, signal?: AbortSignal) {
    const params: any = { CompId };
    if (searchText) {
      params.searchText = searchText;
    }
    return authenticatedAxiosInstance.get(
      `${process.env.REACT_APP_API_URL}/dealer/corporate-docs`,
      { headers: authHeader(), params, signal }
    );
  }

  createCorporateDocs(description: string, compId: string, byUserName: string, file: File) {
    const formData = new FormData();
    formData.append('description', description);
    formData.append('compId', compId.toString());
    formData.append('byUserName', byUserName);
    formData.append('file', file);

    return authenticatedAxiosInstance.post(
      `${process.env.REACT_APP_API_URL}/dealer/corporate-docs`,
      formData,
      {
        headers: {
          ...authHeader(),
          'Content-Type': 'multipart/form-data',
        },
      }
    );
  }

  deleteCorporateDocs(recId: number) {
    return authenticatedAxiosInstance.delete(
      `${process.env.REACT_APP_API_URL}/dealer/corporate-docs`,
      {
        headers: authHeader(),
        data: { recId },
      }
    );
  }

  getDealerBorrowingBaseReports(CompId?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(
      `${process.env.REACT_APP_API_URL}/dealer/borrowing-base`,
      { headers: authHeader(), params: { CompId }, signal }
    );
  }

  releaseDealerBorrowingBaseReports(recid: string) {
    return authenticatedAxiosInstance.post(
      `${process.env.REACT_APP_API_URL}/dealer/borrowing-base/report/release`,
      { recid },
      { headers: authHeader() }
    );
  }

  deleteDealerBorrowingBaseReports(recid: string) {
    return authenticatedAxiosInstance.delete(
      `${process.env.REACT_APP_API_URL}/dealer/borrowing-base/report`,
      {
        headers: authHeader(),
        data: { recid },
      }
    );
  }

  createDealerBorrowingBaseReport(compId: string, dataDate: string) {
    return authenticatedAxiosInstance.post(
      `${process.env.REACT_APP_API_URL}/dealer/borrowing-base/create-report`,
      { compId, dataDate },
      { headers: authHeader() }
    );
  }

  reAmortVRates(Compid: string, Billday: string) {
    return authenticatedAxiosInstance.post(
      `${process.env.REACT_APP_API_URL}/dealer/billing-options/re-amortize-rate`,
      { Compid, Billday },
      {
        headers: authHeader(),
      }
    );
  }

  createFPRate(data: FPRate) {
    return authenticatedAxiosInstance.post(`${process.env.REACT_APP_API_URL}/dealer/fprate`, data, {
      headers: authHeader(),
    });
  }

  updateFPRate(id: number, data: FPRate) {
    return authenticatedAxiosInstance.put(
      `${process.env.REACT_APP_API_URL}/dealer/fprate/${id}`,
      data,
      {
        headers: authHeader(),
      }
    );
  }

  deleteFPRate(RecID: number) {
    return authenticatedAxiosInstance.delete(
      `${process.env.REACT_APP_API_URL}/dealer/fprate/${RecID}`,
      {
        headers: authHeader(),
      }
    );
  }

  getFPRates(CompId: number, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${process.env.REACT_APP_API_URL}/dealer/fprate`, {
      headers: authHeader(),
      params: { CompId },
      signal,
    });
  }

  createLPRate(data: LPRate) {
    return authenticatedAxiosInstance.post(`${process.env.REACT_APP_API_URL}/dealer/lprate`, data, {
      headers: authHeader(),
    });
  }

  updateLPRate(id: number, data: LPRate) {
    return authenticatedAxiosInstance.put(
      `${process.env.REACT_APP_API_URL}/dealer/lprate/${id}`,
      data,
      {
        headers: authHeader(),
      }
    );
  }

  deleteLPRate(RecID: number) {
    return authenticatedAxiosInstance.delete(
      `${process.env.REACT_APP_API_URL}/dealer/lprate/${RecID}`,
      {
        headers: authHeader(),
      }
    );
  }

  sendRateLetters(reportName: string, reportId: number) {
    return authenticatedAxiosInstance.post(
      `${process.env.REACT_APP_API_URL}/dealer/rate/send-letters`,
      { reportId, reportName },
      { headers: authHeader() }
    );
  }

  getLPRates(CompId: number, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${process.env.REACT_APP_API_URL}/dealer/lprate`, {
      headers: authHeader(),
      params: { CompId },
      signal,
    });
  }

  createBBRate(data: BBRate) {
    return authenticatedAxiosInstance.post(`${process.env.REACT_APP_API_URL}/dealer/bbrate`, data, {
      headers: authHeader(),
    });
  }

  updateBBRate(id: number, data: BBRate) {
    return authenticatedAxiosInstance.put(
      `${process.env.REACT_APP_API_URL}/dealer/bbrate/${id}`,
      data,
      {
        headers: authHeader(),
      }
    );
  }

  deleteBBRate(RecID: number) {
    return authenticatedAxiosInstance.delete(
      `${process.env.REACT_APP_API_URL}/dealer/bbrate/${RecID}`,
      {
        headers: authHeader(),
      }
    );
  }

  getBBRates(CompId: number, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${process.env.REACT_APP_API_URL}/dealer/bbrate`, {
      headers: authHeader(),
      params: { CompId },
      signal,
    });
  }

  createOriginationFee(data: OriginationFee) {
    return authenticatedAxiosInstance.post(
      `${process.env.REACT_APP_API_URL}/dealer/originationfee`,
      data,
      { headers: authHeader() }
    );
  }

  updateOriginationFee(id: number, data: OriginationFee) {
    return authenticatedAxiosInstance.put(
      `${process.env.REACT_APP_API_URL}/dealer/originationfee/${id}`,
      data,
      {
        headers: authHeader(),
      }
    );
  }

  deleteOriginationFee(id: number) {
    return authenticatedAxiosInstance.delete(
      `${process.env.REACT_APP_API_URL}/dealer/originationfee/${id}`,
      {
        headers: authHeader(),
      }
    );
  }

  getOriginationFees(CompId: number, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(
      `${process.env.REACT_APP_API_URL}/dealer/originationfee`,
      {
        headers: authHeader(),
        params: { CompId },
        signal,
      }
    );
  }

  createOrgFeeTier(data: OrgFeeTier) {
    return authenticatedAxiosInstance.post(
      `${process.env.REACT_APP_API_URL}/dealer/orgfeetier`,
      data,
      { headers: authHeader() }
    );
  }

  updateOrgFeeTier(id: number, data: OrgFeeTier) {
    return authenticatedAxiosInstance.put(
      `${process.env.REACT_APP_API_URL}/dealer/orgfeetier/${id}`,
      data,
      {
        headers: authHeader(),
      }
    );
  }

  deleteOrgFeeTier(id: number) {
    return authenticatedAxiosInstance.delete(
      `${process.env.REACT_APP_API_URL}/dealer/orgfeetier/${id}`,
      {
        headers: authHeader(),
      }
    );
  }

  getOrgFeeTiers(CompId: number, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${process.env.REACT_APP_API_URL}/dealer/orgfeetier`, {
      headers: authHeader(),
      params: { CompId },
      signal,
    });
  }

  createUnitFeeThreshold(data: UnitFeeThreshold) {
    return authenticatedAxiosInstance.post(
      `${process.env.REACT_APP_API_URL}/dealer/unitfeethreshold`,
      data,
      { headers: authHeader() }
    );
  }

  updateUnitFeeThreshold(id: number, data: UnitFeeThreshold) {
    return authenticatedAxiosInstance.put(
      `${process.env.REACT_APP_API_URL}/dealer/unitfeethreshold/${id}`,
      data,
      {
        headers: authHeader(),
      }
    );
  }

  deleteUnitFeeThreshold(Recid: number) {
    return authenticatedAxiosInstance.delete(
      `${process.env.REACT_APP_API_URL}/dealer/unitfeethreshold/${Recid}`,
      {
        headers: authHeader(),
      }
    );
  }

  getUnitFeeThresholds(CompId: number, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(
      `${process.env.REACT_APP_API_URL}/dealer/unitfeethreshold`,
      {
        headers: authHeader(),
        params: { CompId },
        signal,
      }
    );
  }

  sendEmail(associate: number, dsAuthType: string, dsAuthLevel: string) {
    return authenticatedAxiosInstance.post(
      `${process.env.REACT_APP_API_URL}/dealer/send-email`,
      { associate, dsAuthType, dsAuthLevel },
      { headers: authHeader() }
    );
  }

  getDealerAssociates(CompId: string | number, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${process.env.REACT_APP_API_URL}/dealer/associates`, {
      headers: authHeader(),
      params: { CompId },
      signal,
    });
  }

  /**
   * Get API endpoint documentation for debugging purposes
   * @returns {Promise} - API response with endpoint documentation
   */
  getApiDocumentation(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${API_URL}/docs`, { headers: authHeader(), signal });
  }

  /**
   * Get SQL procedure documentation for debugging purposes
   * @returns {Promise} - API response with SQL procedure documentation
   */
  getSqlDocumentation(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${API_URL}/sql-docs`, { headers: authHeader(), signal });
  }

  /**
   * Get dealer API endpoint documentation for debugging purposes
   * @returns {Promise} - API response with dealer endpoint documentation
   */
  getDealerApiDocumentation(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${process.env.REACT_APP_API_URL}/dealer/docs`, {
      headers: authHeader(),
      signal,
    });
  }

  /**
   * Get dealer SQL procedure documentation for debugging purposes
   * @returns {Promise} - API response with dealer SQL procedure documentation
   */
  getDealerSqlDocumentation(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${process.env.REACT_APP_API_URL}/dealer/sql-docs`, {
      headers: authHeader(),
      signal,
    });
  }

  /**
   * Get rate notification API endpoint documentation for debugging purposes
   * @returns {Promise} - API response with rate notification endpoint documentation
   */
  getRateNotificationApiDocumentation(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(
      `${process.env.REACT_APP_API_URL}/ratenotification/docs`,
      { headers: authHeader(), signal }
    );
  }

  /**
   * Get rate notification SQL procedure documentation for debugging purposes
   * @returns {Promise} - API response with rate notification SQL procedure documentation
   */
  getRateNotificationSqlDocumentation(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(
      `${process.env.REACT_APP_API_URL}/ratenotification/sql-docs`,
      { headers: authHeader(), signal }
    );
  }

  // ===========================================
  // Unit Projections Methods
  // ===========================================

  /**
   * Get projections for a company, optionally filtered by year
   * @param {string} companyId - Company ID
   * @param {number} [year] - Optional year filter
   * @returns {Promise} - API response with projections data
   */
  getProjections(companyId: string, year?: number, signal?: AbortSignal) {
    const params: any = { companyId };
    if (year) {
      params.year = year;
    }
    return authenticatedAxiosInstance.get(
      `${process.env.REACT_APP_API_URL}/dealer/unit-projections`,
      {
        headers: authHeader(),
        params,
        signal,
      }
    );
  }

  /**
   * Get projection summary statistics
   * @param {string} companyId - Company ID
   * @param {number} [year] - Optional year filter
   * @returns {Promise} - API response with summary statistics
   */
  getProjectionsSummary(companyId: string, year?: number, signal?: AbortSignal) {
    const params: any = { companyId };
    if (year) {
      params.year = year;
    }
    return authenticatedAxiosInstance.get(
      `${process.env.REACT_APP_API_URL}/dealer/unit-projections/summary`,
      {
        headers: authHeader(),
        params,
        signal,
      }
    );
  }

  /**
   * Save (create or update) multiple projections
   * @param {string} companyId - Company ID
   * @param {Array} projections - Array of projection data
   * @returns {Promise} - API response with saved projections
   */
  saveProjections(companyId: string, projections: any[]) {
    return authenticatedAxiosInstance.post(
      `${process.env.REACT_APP_API_URL}/dealer/unit-projections`,
      { companyId, projections },
      { headers: authHeader() }
    );
  }

  /**
   * Delete a projection by ID
   * @param {string} id - Projection ID
   * @returns {Promise} - API response confirming deletion
   */
  deleteProjection(id: string) {
    return authenticatedAxiosInstance.delete(
      `${process.env.REACT_APP_API_URL}/dealer/unit-projections/${id}`,
      { headers: authHeader() }
    );
  }
}

export default new CompanyService();
